import { create } from 'zustand';
import {
  resolutionApi,
  type CanonicalEntityData,
  type CanonicalSubgraph,
  type ResolutionResultData,
  type ResolutionStats,
} from '@/services/api';

export type KnowledgeTab =
  | 'overview'
  | 'ontology'
  | 'catalogs'
  | 'graph'
  | 'history'
  | 'sources';

interface KnowledgeFilters {
  entityType: string;
  ontologicalClass: string;
  search: string;
}

interface KnowledgeState {
  canonicals: CanonicalEntityData[];
  selectedCanonical: CanonicalEntityData | null;
  subgraph: CanonicalSubgraph | null;
  resolutionHistory: ResolutionResultData[];
  stats: ResolutionStats | null;
  filters: KnowledgeFilters;
  activeTab: KnowledgeTab;
  totalCount: number;
  offset: number;

  entityTypes: string[];
  ontologyDistribution: Array<{ id: string; count: number }>;

  loading: boolean;
  detailLoading: boolean;
  graphLoading: boolean;
  historyLoading: boolean;
  statsLoading: boolean;
  error: string | null;

  selectedIds: Set<string>;
  bulkClassifyRunning: boolean;

  setFilters: (filters: Partial<KnowledgeFilters>) => void;
  setActiveTab: (tab: KnowledgeTab) => void;
  selectCanonical: (canonical: CanonicalEntityData | null) => void;

  fetchCanonicals: () => Promise<void>;
  fetchMore: () => Promise<void>;
  fetchCanonicalDetail: (id: string) => Promise<void>;
  fetchSubgraph: (id: string, depth?: number) => Promise<void>;
  fetchHistory: (id: string) => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchEntityTypes: () => Promise<void>;
  fetchOntologyDistribution: () => Promise<void>;

  toggleSelection: (id: string) => void;
  clearSelection: () => void;
  classifySelected: () => Promise<void>;
}

const PAGE_SIZE = 50;

// Debounce handle for semantic search — module-level so successive edits cancel cleanly.
let searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

export const useKnowledgeStore = create<KnowledgeState>((set, get) => ({
  canonicals: [],
  selectedCanonical: null,
  subgraph: null,
  resolutionHistory: [],
  stats: null,
  filters: { entityType: '', ontologicalClass: '', search: '' },
  activeTab: 'overview',
  totalCount: 0,
  offset: 0,

  entityTypes: [],
  ontologyDistribution: [],

  loading: false,
  detailLoading: false,
  graphLoading: false,
  historyLoading: false,
  statsLoading: false,
  error: null,

  selectedIds: new Set<string>(),
  bulkClassifyRunning: false,

  setFilters: (partial) => {
    const prev = get().filters;
    const next = { ...prev, ...partial };
    set({ filters: next, offset: 0 });

    // Non-search changes fetch immediately; search is debounced.
    const changedKeys = Object.keys(partial);
    const onlySearchChanged =
      changedKeys.length === 1 && changedKeys[0] === 'search';

    if (onlySearchChanged) {
      if (searchDebounceHandle) clearTimeout(searchDebounceHandle);
      searchDebounceHandle = setTimeout(() => {
        get().fetchCanonicals();
      }, 300);
    } else {
      if (searchDebounceHandle) {
        clearTimeout(searchDebounceHandle);
        searchDebounceHandle = null;
      }
      get().fetchCanonicals();
    }
  },

  setActiveTab: (tab) => set({ activeTab: tab }),

  selectCanonical: (canonical) => {
    set({ selectedCanonical: canonical, subgraph: null, resolutionHistory: [], activeTab: 'overview' });
    if (canonical) {
      get().fetchSubgraph(canonical.id);
      get().fetchHistory(canonical.id);
    }
  },

  fetchCanonicals: async () => {
    const { filters } = get();
    set({ loading: true, error: null, offset: 0 });
    try {
      // Semantic search path — replaces plain list when the user is actively searching.
      if (filters.search.trim()) {
        const results = await resolutionApi.searchSimilar(
          filters.search,
          filters.entityType || undefined,
          50,
        );
        // searchSimilar returns an array of loosely-typed rows; coerce the ones
        // whose shape matches CanonicalEntityData. Unknown rows pass through untouched.
        const coerced = (results.results as CanonicalEntityData[]).filter(
          (r) => r && r.id && r.canonical_name,
        );

        // Client-side UOS class filter on top of semantic results.
        const filtered = filters.ontologicalClass
          ? coerced.filter((c) => c.ontological_class === filters.ontologicalClass)
          : coerced;

        set({ canonicals: filtered, totalCount: filtered.length, loading: false });
        return;
      }

      // UOS class filter (server-side) when no free-text search is active.
      if (filters.ontologicalClass) {
        const res = await resolutionApi.getClassCanonicals(
          filters.ontologicalClass,
          PAGE_SIZE,
          0,
        );
        // Normalize to CanonicalEntityData — class endpoint returns a trimmed row.
        const normalized = res.canonicals.map((c) => ({
          id: c.id,
          canonical_name: c.canonical_name,
          entity_type: c.entity_type,
          canonical_data: {},
          source_entity_ids: [],
          source_entity_count: c.source_entity_count,
          confidence: c.confidence,
          completeness: 0,
          data_quality_score: 0,
          version: 0,
          created_at: '',
          updated_at: '',
          ontological_class: res.ontological_class,
          ontological_classification_confidence:
            c.ontological_classification_confidence,
        })) as CanonicalEntityData[];

        const final = filters.entityType
          ? normalized.filter((c) => c.entity_type === filters.entityType)
          : normalized;

        set({ canonicals: final, totalCount: res.count, loading: false });
        return;
      }

      const res = await resolutionApi.listCanonicals({
        entity_type: filters.entityType || undefined,
        limit: PAGE_SIZE,
        offset: 0,
      });
      set({ canonicals: res.canonicals, totalCount: res.count, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchMore: async () => {
    const { filters, offset, canonicals } = get();
    // Semantic search and UOS class endpoints don't paginate in Phase 0.
    if (filters.search.trim() || filters.ontologicalClass) return;

    const nextOffset = offset + PAGE_SIZE;
    set({ loading: true });
    try {
      const res = await resolutionApi.listCanonicals({
        entity_type: filters.entityType || undefined,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      set({
        canonicals: [...canonicals, ...res.canonicals],
        offset: nextOffset,
        loading: false,
      });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchCanonicalDetail: async (id) => {
    set({ detailLoading: true });
    try {
      const canonical = await resolutionApi.getCanonical(id);
      set({ selectedCanonical: canonical, detailLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, detailLoading: false });
    }
  },

  fetchSubgraph: async (id, depth = 2) => {
    set({ graphLoading: true });
    try {
      const subgraph = await resolutionApi.getCanonicalGraph(id, depth);
      set({ subgraph, graphLoading: false });
    } catch {
      set({ graphLoading: false });
    }
  },

  fetchHistory: async (id) => {
    set({ historyLoading: true });
    try {
      const res = await resolutionApi.getCanonicalHistory(id);
      set({ resolutionHistory: res.results, historyLoading: false });
    } catch {
      set({ historyLoading: false });
    }
  },

  fetchStats: async () => {
    const { filters } = get();
    set({ statsLoading: true });
    try {
      const stats = await resolutionApi.getStats(filters.entityType || undefined);
      set({ stats, statsLoading: false });
    } catch {
      set({ statsLoading: false });
    }
  },

  fetchEntityTypes: async () => {
    try {
      const res = await resolutionApi.getDistinctEntityTypes();
      set({ entityTypes: res.entity_types });
    } catch {
      // Degrade silently — the filter just shows "All Types" until types populate.
      set({ entityTypes: [] });
    }
  },

  fetchOntologyDistribution: async () => {
    try {
      const res = await resolutionApi.getOntologicalDistribution();
      const rows = Object.entries(res.distribution || {}).map(([id, count]) => ({
        id,
        count,
      }));
      rows.sort((a, b) => b.count - a.count);
      set({ ontologyDistribution: rows });
    } catch {
      set({ ontologyDistribution: [] });
    }
  },

  toggleSelection: (id) => {
    const next = new Set(get().selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    set({ selectedIds: next });
  },

  clearSelection: () => set({ selectedIds: new Set() }),

  classifySelected: async () => {
    const { selectedIds } = get();
    if (selectedIds.size === 0) return;
    set({ bulkClassifyRunning: true });
    try {
      // Fire classification requests in parallel — backend classification is async.
      await Promise.allSettled(
        Array.from(selectedIds).map((id) => resolutionApi.classifyCanonical(id)),
      );
    } finally {
      set({ bulkClassifyRunning: false, selectedIds: new Set() });
    }
  },
}));
